import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Target, Sparkles, Cloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function CareerVisionGuide() {
  const navigate = useNavigate();

  const visionPrompts = [
    "Where are you working? (office, home, travel, outdoors)",
    "What time do you start your day? How do you begin?",
    "What kind of work are you doing? What projects excite you?",
    "Who are you working with? Team, clients, independently?",
    "What's your energy level throughout the day?",
    "How much creative freedom do you have?",
    "What's your income? How does money feel?",
    "How do you feel when your workday ends?"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <Eye className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Career Vision Exercise</h1>
          <p className="text-lg text-secondary-600">Visualize your ideal professional future</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Power of Vision</h2>
          <p className="text-secondary-700 mb-4">
            When you clearly visualize your ideal career, your brain's Reticular Activating System (RAS) starts filtering information to help you notice opportunities aligned with that vision. What you focus on expands.
          </p>
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <p className="text-indigo-800 font-medium">
              🧠 Research shows visualization activates the same brain regions as actually performing the activity, making your vision feel more achievable.
            </p>
          </div>
        </section>

        <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border-2 border-indigo-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Cloud className="w-6 h-6 text-indigo-500" />
            Guided Visualization
          </h2>
          <p className="text-secondary-700 mb-4">Find a quiet place, close your eyes, and imagine your ideal workday ONE YEAR from now:</p>
          <div className="space-y-3">
            {visionPrompts.map((prompt, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                <Sparkles className="w-5 h-5 text-indigo-400 shrink-0" />
                <span className="text-secondary-700">{prompt}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-500" />
            Write Your Vision
          </h2>
          <p className="text-secondary-700 mb-4">After visualizing, write it down in detail. Use present tense as if it's already happening:</p>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-green-800 italic">
              "It's [date one year from now]. I wake up feeling excited about my work. I [describe your morning routine]. My workspace is [describe it]. Today I'm working on [describe your projects]. I feel [emotions] because [reasons]. My income is [amount] and I feel [how money feels]. At the end of my day, I [describe how you close work]..."
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Making It Real</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="font-bold text-amber-800 mb-2">🎯 Identify the Gap</h3>
              <p className="text-amber-900 text-sm">What's different between now and your vision? What skills, connections, or changes are needed?</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-2">👣 One First Step</h3>
              <p className="text-blue-900 text-sm">What's ONE small action you could take this week to move toward your vision?</p>
            </div>
          </div>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What surprised me about my career vision? What's one thing I saw clearly that I hadn't consciously acknowledged wanting?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default CareerVisionGuide;
